import {
  getKeyByPostedComment,
  getKeyByPostedCommentSnippet,
  getKeyByPostedReview,
  getPostedEntityKey,
  PostedEntityRequest,
} from './generators';
import { emptyResults, emptyIndirectAPIData } from 'Common/utilities/apidata';
import { getStore } from 'stores/common/selectors';
import {
  PostedComment,
  PostedCommentSnippet,
  PostedReview,
  PullRequest,
} from './models';
import { APIData, IndirectAPIData, PaginatedAPIData } from 'utils/apidata';
import { selectors as contributorsSelectors } from 'stores/organizations/contributors';
import { selectors as billingSelectors } from 'reducers/billing';
import { selectors as active } from './active';
import {
  organizationSelector,
  isOrganizationOwner,
} from 'selectors/Organization';
import { selectors as pullRequestsSelectors } from 'reducers/pullRequests';
import { Organization } from 'stores/organizations/models';
import {
  emptyIndirectPaginatedAPIData,
  emptyPaginatedAPIData,
  listEntries,
  listMapEntryData,
} from 'Common/utilities/pagination';
import { createSelector } from 'reselect';

export function entries(state: any) {
  const store = getStore(state);
  return store.pullRequests.entries ?? emptyIndirectAPIData;
}

export function entry(state: any, uuid?: string): APIData<PullRequest> {
  if (uuid === undefined) {
    return emptyResults;
  }
  const prEntries = entries(state);
  return prEntries[uuid] ?? emptyResults;
}

export function entryData(state: any, uuid?: string): PullRequest | undefined {
  const prEntry = entry(state, uuid);
  if (!prEntry.isFilled || !prEntry.success) {
    return undefined;
  }
  return prEntry.data;
}

export function isToggling(state: any, uuid?: string): boolean {
  if (uuid === undefined) {
    return false;
  }

  const store = getStore(state);
  const toggleEntries = store.pullRequests.toggleReview[uuid] || emptyResults;
  return toggleEntries.isLoading;
}

export const entryError = (
  state: any,
  uuid?: string
): PullRequest | undefined => {
  if (uuid === undefined) {
    return undefined;
  }

  const store = getStore(state);
  const toggleEntries = store.pullRequests.toggleReview[uuid] || emptyResults;
  return !toggleEntries.success ? toggleEntries.errorData : undefined;
};

export function canReview(state: any, uuid?: string): boolean {
  if (uuid === undefined) {
    return false;
  }

  const pullRequest = entryData(state, uuid);
  if (pullRequest === undefined) {
    return false;
  }

  const organization: Organization | undefined = organizationSelector(state);
  if (!organization) {
    // This isn't really a possibility unless there isn't an organization that
    // can be inferred from the current route.
    return false;
  }

  // For organizations with a statically assigned number of users, check to see
  // if there are available seats or if there is already a contributor with a
  // seat for the author's username.
  const isStaticUsers = billingSelectors.isStaticUsers(
    state,
    organization.uuid
  );
  const contributor = contributorsSelectors.contributor(
    state,
    pullRequest.user_name
  );
  const isBilled = Boolean(contributor && contributor.has_seat);
  const availableSeats = billingSelectors.getAvailableSeats(
    state,
    organization.uuid
  );
  if (isStaticUsers && !availableSeats && !isBilled) {
    return false;
  }

  // For opt-in billing, when a review is submitted for a non-billed
  // contributor, a new seat will be added to the subscription, and the author
  // in question will be added as a seat. In this case, if the requester isn't
  // an organization owner, we need to return false here because they won't be
  // able to add the author as a contributor.
  const isOptInBilling = billingSelectors.isOptInBilling(
    state,
    organization.uuid
  );
  const isOwner = isOrganizationOwner(state);
  if (isOptInBilling && !isStaticUsers && !isBilled && !isOwner) {
    return false;
  }

  return true;
}

export function postedCommentsPages(
  state: any,
  request: PostedEntityRequest
): PaginatedAPIData<PostedComment> {
  const key = getPostedEntityKey(request);
  const store = getStore(state);
  const postedCommentsStore =
    store.pullRequests.postedComments ?? emptyIndirectPaginatedAPIData;
  return postedCommentsStore[key] ?? emptyPaginatedAPIData;
}

export function postedCommentsEntries(state: any) {
  const store = getStore(state);
  return store.pullRequests.postedCommentsEntries ?? emptyIndirectAPIData;
}

export const postedComments = createSelector(
  [postedCommentsPages, postedCommentsEntries],
  (
    pages: PaginatedAPIData<PostedComment>,
    entries: IndirectAPIData<PostedComment>
  ) => listMapEntryData(entries, getKeyByPostedComment, listEntries(pages))
);

export function postedCommentSnippetsPages(
  state: any,
  request: PostedEntityRequest
): PaginatedAPIData<PostedCommentSnippet> {
  const key = getPostedEntityKey(request);
  const store = getStore(state);
  const postedCommentSnippetsStore =
    store.pullRequests.postedCommentSnippets ?? emptyIndirectPaginatedAPIData;
  return postedCommentSnippetsStore[key] ?? emptyPaginatedAPIData;
}

export function postedCommentSnippetsEntries(state: any) {
  const store = getStore(state);
  return (
    store.pullRequests.postedCommentSnippetsEntries ?? emptyIndirectAPIData
  );
}

export const postedCommentSnippets = createSelector(
  [postedCommentSnippetsPages, postedCommentSnippetsEntries],
  (
    pages: PaginatedAPIData<PostedCommentSnippet>,
    entries: IndirectAPIData<PostedCommentSnippet>
  ) =>
    listMapEntryData(entries, getKeyByPostedCommentSnippet, listEntries(pages))
);

export function postedReviewsPages(
  state: any,
  request: PostedEntityRequest
): PaginatedAPIData<PostedReview> {
  const key = getPostedEntityKey(request);
  const store = getStore(state);
  const postedReviewsStore =
    store.pullRequests.postedReviews ?? emptyIndirectPaginatedAPIData;
  return postedReviewsStore[key] ?? emptyPaginatedAPIData;
}

export function postedReviewsEntries(state: any) {
  const store = getStore(state);
  return store.pullRequests.postedReviewsEntries ?? emptyIndirectAPIData;
}

export const postedReviews = createSelector(
  [postedReviewsPages, postedReviewsEntries],
  (
    pages: PaginatedAPIData<PostedReview>,
    entries: IndirectAPIData<PostedReview>
  ) => listMapEntryData(entries, getKeyByPostedReview, listEntries(pages))
);

export const reviews = (state: any, request: PostedEntityRequest) => {
  const { providerId, organizationSlug, projectName, reviewNumber } = request;
  const otherReviews =
    pullRequestsSelectors.pullRequestReviewsForOrgAndRepo(
      state,
      providerId,
      organizationSlug,
      projectName,
      reviewNumber
    ) ?? [];
  return [...postedReviews(state, request), ...otherReviews];
};

export const selectors = {
  entries,
  entry,
  entryData,
  entryError,
  isToggling,
  canReview,
  reviews,
  postedComments,
  postedCommentsEntries,
  postedCommentsPages,
  postedCommentSnippets,
  postedReviews,
  active,
};
